import React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';

export interface TimelineStep {
  title: string;
  meta?: string;
  /** `failed` marks a step that didn't succeed, e.g. a missed delivery attempt. */
  state: 'done' | 'current' | 'pending' | 'failed';
}

/** Vertical progress steps (Figma "Timeline step"): done, current, failed and pending. */
const Timeline: React.FC<{ steps: TimelineStep[]; className?: string }> = ({ steps, className }) => (
  <ol className={cn('tw-flex tw-flex-col', className)}>
    {steps.map((step, i) => {
      const last = i === steps.length - 1;
      return (
        <li key={step.title} aria-current={step.state === 'current' ? 'step' : undefined} className="tw-flex tw-gap-[14px]">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-self-stretch">
            {step.state === 'done' ? (
              <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-ds-brand tw-text-ds-on-brand">
                <Icon icon={Check} size={14} />
              </span>
            ) : step.state === 'failed' ? (
              <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-ds-danger-bg tw-text-ds-danger-fg">
                <Icon icon={X} size={14} />
              </span>
            ) : step.state === 'current' ? (
              <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-ds-brand-tint">
                <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-ds-brand" />
              </span>
            ) : (
              <span className="tw-h-6 tw-w-6 tw-rounded-full tw-border tw-border-ds-border-strong tw-bg-ds-surface" />
            )}
            {!last && (
              <span className={cn('tw-w-0.5 tw-flex-1', step.state === 'done' ? 'tw-bg-ds-brand' : 'tw-bg-ds-border-default')} />
            )}
          </div>
          <div className={cn('tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px] tw-pt-0.5', !last && 'tw-pb-[22px]')}>
            <p
              className={cn(
                'tw-text-[15px] tw-font-semibold tw-leading-5',
                step.state === 'pending'
                  ? 'tw-text-ds-text-tertiary'
                  : step.state === 'failed'
                    ? 'tw-text-ds-danger-fg'
                    : 'tw-text-ds-text-primary',
              )}
            >
              {step.title}
            </p>
            {step.meta && (
              <p
                className={cn(
                  'tw-text-[13px] tw-leading-[18px]',
                  step.state === 'current' ? 'tw-font-semibold tw-text-ds-brand-text' : 'tw-text-ds-text-secondary',
                )}
              >
                {step.meta}
              </p>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);

export default Timeline;
